/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useMemo } from 'react';

const DRAWER_OFFSET_PX = 60;
export const BASE_Z_INDEX = 1000;
const ANIMATION_DURATION_MS = 300;

export interface DrawerStackContextValue {
  /** Ordered list of currently open drawer names */
  openDrawers: string[];
  /** Width of all drawers in the stack */
  drawerWidth: string;
  /** Offset in px */
  offsetPx: number;
  /** Animation duration in ms */
  animationDurationMs: number;
}

const DrawerStackContext = createContext<DrawerStackContextValue | null>(null);

export interface DrawerStackProviderProps {
  children: React.ReactNode;
  /** Width of all drawers in the stack (e.g. '438px') */
  drawerWidth: string;
  /** Ordered list of currently open drawer names (first opened = bottom) */
  openDrawers: string[];
  /** Offset in px between stacked drawers (default: 60) */
  offsetPx?: number; // eslint-disable-line react/require-default-props
  /** Animation duration in ms (default: 300) */
  animationDurationMs?: number; // eslint-disable-line react/require-default-props
}

export const DrawerStackProvider: React.FC<DrawerStackProviderProps> = ({
  children,
  drawerWidth,
  openDrawers,
  offsetPx = DRAWER_OFFSET_PX,
  animationDurationMs = ANIMATION_DURATION_MS,
}) => {
  const value = useMemo<DrawerStackContextValue>(() => ({
    openDrawers,
    drawerWidth,
    offsetPx,
    animationDurationMs,
  }), [openDrawers, drawerWidth, offsetPx, animationDurationMs]);

  return (
    <DrawerStackContext.Provider value={value}>
      {children}
    </DrawerStackContext.Provider>
  );
};

export const useDrawerStackContext = (): DrawerStackContextValue => {
  const ctx = useContext(DrawerStackContext);
  if (!ctx) {
    throw new Error('useDrawerStackContext must be used within a DrawerStackProvider');
  }
  return ctx;
};

/**
 * Same as `useDrawerStackContext` but returns `null` instead of throwing
 * when no `DrawerStackProvider` is mounted. Use this inside components
 * that want to self-wrap with a default provider when used standalone.
 *
 * @returns {DrawerStackContextValue | null} The context value, or `null` when not inside a provider
 */
export const useDrawerStackContextOptional = (): DrawerStackContextValue | null => useContext(DrawerStackContext);

// Compute offset for a drawer: count how many drawers are above it in the open stack
export const computeStackOffset = (
  name: string,
  openStack: string[],
  perDrawerOffsetPx: number
): number => {
  const index = openStack.indexOf(name);
  if (index === -1) return 0;
  const drawersAbove = openStack.length - 1 - index;
  return drawersAbove * perDrawerOffsetPx;
};

// Compute z-index for a drawer based on its position in the open stack
export const computeStackZIndex = (
  name: string,
  openStack: string[]
): number => {
  const index = openStack.indexOf(name);
  if (index === -1) return BASE_Z_INDEX;
  return BASE_Z_INDEX + index;
};

// Check if a drawer is the topmost in the open stack
export const isTopmostDrawer = (
  name: string,
  openStack: string[]
): boolean => {
  if (openStack.length === 0) return false;
  return openStack[openStack.length - 1] === name;
};
